import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Button, Layout, Collapse, Spin, message } from 'antd';
import Link from 'next/link'
import _ from 'lodash';
import {distanceInWordsToNow} from 'date-fns';
import useSWR from 'swr';

import Cover from '../story/Cover';
import StoryTags from '../story/StoryTags';
import CommonSwitch from '../common/Switch';
import MyComment from '../comment';
import FollowButton from './FollowButton';
import AutoHideScrollable from '../common/AutoHideScrollable';
import NsfwTag from '../common/NsfwTag';
import AdultCheck from '../common/AdultCheck';
import EnabledButton from '../common/EnabledButton';

import {callApi, storyUrl} from '../../helpers/Api';
import { cleanNumber, wordCountDiffMin, wordCountToMin } from '../../helpers/NumberFormat';
import NoStretchDiv from '../../helpers/NoStretchDiv';
import UserContext from '../../context/UserContext';
import BasicModal from '../common/BasicModal';
import Icon from '../icon/Icon';
import Report from '../forms/Report';
const { Content } = Layout;
const { Panel } = Collapse;

const Book = ({ storyData, admin, role, commentId }) => {
    const {loggedInUser} = useContext(UserContext);
    const [loading, setLoading] = useState(true);
    const [liked, setLiked] = useState(false);
    const [likes, setLikes] = useState(0);
    const [saved, setSaved] = useState(false);
    const [saves, setSaves] = useState(0);
    const [story, setStory] = useState(null);
    const [editing, setEditing] = useState(false);
    const storyId = storyData.id;
    const storyUuid = storyData.uuid;
    const { data: versions } = useSWR('/api/story_version/book/' + role + '/' + storyUuid);
    useEffect(() => {
        if(loggedInUser) {
            callApi('/api/own/story_like_save/' + storyUuid)
            .then(values => {
                if(values) {
                    setLiked(values[0]?.liked)
                    setSaved(values[0]?.saved)
                }
            })
        }
    }, [loggedInUser])

    useEffect(() => {
        if(versions) {
            setLoading(false)
            setStory(versions[0])
            setLikes(liked && versions[0].story_like_count === 0 ? 1 : versions[0].story_like_count)
            setSaves(saved && versions[0].story_save_count === 0 ? 1 : versions[0].story_save_count)
    
            if(admin && versions[0].user_account_id === loggedInUser?.id) {
                setEditing(true);
            }
        }
    }, [admin, versions, loggedInUser, liked, saved])

    const handlePublish = (version, i) => {
        callApi('/api/own/story_version/' + version.story_version_id + '/publish', {
            method: 'PUT',
            body: JSON.stringify({published: !version.published})
        })
        .then(
            versions[i].published = !version.published
        )
        .catch(err => {console.log(err)});
    }

    return (
        loading ? (
            <Spin tip="Loading..."></Spin>
        ) : (
        <AdultCheck is_adult_content={story.is_nsfw}>
            <NoStretchDiv>
                <Content style={{padding: '5%'}}>
                    <Row>
                        {editing ? (
                            <div>
                                <Button 
                                    className="medium-darkable-button fable-button-inverse"
                                    size='small'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const newIsNsfw = !story.is_nsfw;
                                        callApi('/api/own/story/' + storyId,{
                                            method: 'PUT',
                                            body: JSON.stringify({
                                                is_nsfw: newIsNsfw,
                                            })
                                        })
                                        .then(()=>{
                                            var temp = _.cloneDeep(story);
                                            temp.is_nsfw = newIsNsfw;
                                            setStory(temp)
                                        });
                                    }}
                                >
                                    {story.is_nsfw ? 'unmark nsfw' : 'mark nsfw'}
                                </Button>
                            </div>
                        ) : null}
                        <h2>{story.is_nsfw ? <NsfwTag /> : null}{story.title}</h2>
                        <p>
                            <Link href="/profile/[username]" as={'/profile/' + story.username}>
                                <a>{story.username}</a>
                            </Link>
                            &nbsp;- {cleanNumber(story.story_read_count + story.story_read_anon_count)} reads - published {distanceInWordsToNow(story.publish_date)} ago
                            &nbsp;
                            {!loggedInUser || story.user_account_id !== loggedInUser.id ? (
                                <FollowButton 
                                    user_account_id={story.user_account_id}
                                />
                            ) : null}
                        </p>
                    </Row>
                    <Row>
                        <Col className="mobile-stretch gutter-row" span={12} push={12} style={{textAlign: 'center'}}>
                            <div style={{paddingLeft: '20%', paddingRight: '20%', paddingBottom: '10px'}}>
                                <Cover 
                                    isAdultContent={story.is_nsfw}
                                    storyUuid={storyUuid}
                                    linkCovertoStory={!editing}
                                    storyVersion={story.story_version}
                                    title={story.title}
                                    author={story.username}
                                    imageSrc={story.cover}
                                    editing={editing}
                                />
                            </div>
                            <div>
                                <StoryTags storyId={storyId} admin={editing}/>
                            </div>
                        </Col>
                        <Col className="mobile-stretch gutter-row" span={12} pull={12}>
                            {editing && !story.published ? (
                                <Button type="primary" size='large' block style={{marginBottom: '12px'}} onClick={()=>{message.info('Story is not published! Change that below or edit in create mode.')}}>
                                    {story.read_date ? 'continue reading latest version' : 'read latest version'}
                                </Button>
                            ) : (
                                <Link href="/read/[storyUuid]/[storyVersion]/[title]" as={'/read/' + storyUuid + '/' + story.story_version + '/' + storyUrl(story.title)}>
                                    <Button type="primary" size='large' block style={{marginBottom: '12px'}}>
                                        {story.read_date ? 'continue reading latest version' : 'read latest version'}
                                    </Button>
                                </Link>
                            )}
                            <Row gutter={16}>
                                <Col className="gutter-row" span={11}>
                                    <EnabledButton 
                                        showCount
                                        showDivider
                                        outsideButton
                                        enabledIcon="heart"
                                        enabledIconColor="red"
                                        tooltipTitle="loved it"
                                        tooltipPlacement="bottom"
                                        getInitialCount={(callback)=>{callback(likes)}}
                                        enabled={liked}
                                        handleEnabled={(newEnabled, callback)=>{
                                            callApi('/api/own/upsert/story_like', {
                                                method: 'POST',
                                                body: JSON.stringify({story_id: story.story_id, liked: newEnabled})
                                            })
                                            .then(callback(newEnabled))
                                            .catch(err => {console.log(err)});
                                        }}
                                    />
                                </Col>
                                <Col className="gutter-row" span={11}>
                                    <EnabledButton 
                                        showCount
                                        showDivider
                                        outsideButton
                                        enabledIcon="bookmark"
                                        enabledIconColor="cyan"
                                        tooltipTitle="save it"
                                        tooltipPlacement="bottom"
                                        getInitialCount={(callback)=>{callback(saves)}}
                                        enabled={saved}
                                        handleEnabled={(newEnabled, callback)=>{
                                            callApi('/api/own/upsert/story_save', {
                                                method: 'POST',
                                                body: JSON.stringify({story_id: story.story_id, saved: newEnabled})
                                            })
                                            .then(callback(newEnabled))
                                            .catch(err => {console.log(err)});
                                        }}
                                    />
                                </Col>
                                <Col className="gutter-row" span={2}>
                                    <BasicModal 
                                        title={"Report " + story.title}
                                        content={
                                            <Report storyUuid={story.story_uuid} />
                                        }
                                        opener={
                                            <div className="clickable" style={{height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                                <Icon style={{verticalAlign: 'middle'}} name="exclamation-triangle"/>
                                            </div>
                                        }
                                        outerStyle={{display: 'inline'}}
                                        innerClass="medium-darkable-modal"
                                        // defaultOpen={props.defaultOpen ? props.defaultOpen : false}
                                        footer={false}
                                    />
                                </Col>
                            </Row>

                            <p>{story.description}</p>
                            <p>Up to {story.max_node_depth} pages deep and {
                                (wordCountDiffMin(story.max_word_count, story.min_word_count) ? 
                                    '~' + wordCountToMin(story.max_word_count) + ' mins'
                                : 
                                    '~' + wordCountToMin(story.min_word_count)
                                    + ' - ' +
                                    wordCountToMin(story.max_word_count) + ' mins'
                                ) + ' read'
                            }</p>
                            <p>Found {story.read_endings?.length || 0} of {story.endings?.length} endings</p>
                            {story.achievement_count > 0 ? (
                                <p>Found {story.read_achievements?.length || 0} of {story.achievement_count} achievements</p>
                            ) : null}
                            {story.has_random ? (
                                <p>Has random component</p>
                            ) : null}

                            {versions.length > 1 || editing ? (
                            <div>
                                <p>All Versions: </p>
                                <AutoHideScrollable onHover={true}>
                                    <div style={{maxHeight:'236px', overflow:'overlay', overflowX: 'hidden'}}>
                                        <Collapse className="medium-darkable" accordion style={{width:'calc(100% - 3px)'}} >
                                            {versions.map((version, i) => 
                                                <Panel 
                                                    className="medium-darkable" 
                                                    header={
                                                        <div className="medium-darkable" > 
                                                            {version.read_date ? (
                                                                version.story_version + ' - ' + 
                                                                cleanNumber(version.story_version_read_count + version.story_version_read_anon_count) + 
                                                                ' reads - last read: ' + distanceInWordsToNow(version.read_date)
                                                            ) : (
                                                                version.story_version + ' - ' + 
                                                                cleanNumber(version.story_version_read_count) + ' reads'
                                                            )}
                                                        </div>
                                                        } 
                                                    key={version.story_version}
                                                >
                                                    <div className='light-darkable'>
                                                        {editing && !version.published ? (
                                                            <Button className="medium-darkable" size='small' block style={{marginBottom: '12px'}}>
                                                                publish below to read
                                                            </Button>
                                                        ) : (
                                                        <Link href="/read/[storyUuid]/[storyVersion]/[title]" as={'/read/' + version.story_uuid + '/' + version.story_version + '/' + storyUrl(story.title)}>
                                                            <Button className="medium-darkable" size='small' block style={{marginBottom: '12px'}}>
                                                                {version.read_date ? 'continue reading' : 'read this version'}
                                                            </Button>
                                                        </Link>
                                                        )}
                                                        {versions.length - 1 === i ? <p>first version</p> : (
                                                            <p>{version.changelog ? version.changelog : 'no changes documented'}</p>
                                                        )}
                                                        {editing ?
                                                            <p>
                                                                <CommonSwitch
                                                                    checked={version.published}
                                                                    onClick={() => {
                                                                        handlePublish(version, i);
                                                                    }}
                                                                /> 
                                                                &nbsp; publish
                                                            </p> 
                                                        : null}
                                                    </div>
                                                </Panel>
                                            )}
                                        </Collapse>
                                    </div>
                                </AutoHideScrollable>
                            </div>
                            ) : null}
                        </Col>
                    </Row>
                    <Row>
                        <MyComment 
                            storyId={storyId}
                            authorId={story.user_account_id}
                            scrollTo={commentId}
                        />
                    </Row>
                </Content>
                {/* <Footer>Footer</Footer> */}
            </NoStretchDiv>
        </AdultCheck>
        )
    );
}

export default Book;